import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Row,
  Col,
  Space,
  Form,
  ColorPicker,
  Select,
  Upload,
  Input,
  Card,
} from "antd";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { GetDataFromServer, updateUserDataOnServer } from "../../lib/api";
import { AGENT_LIST, AGENT_UPDATE_UI } from "../../lib/config";
import {
  beforeUpload,
  UploadButton,
  uploadImages,
} from "../../lib/global_functions";
import ImgCrop from "antd-img-crop";
import { PlusOutlined } from "@ant-design/icons";
import UserInfo from "./user_info";

const { Text } = Typography;

export default function AgentUpdateUI() {
  const { access_token, csrf_token, ui_size } = useSelector(
    (state) => state.user
  );
  const [loading, setLoading] = useState(false);
  const [agentData, setAgentData] = useState(null);
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState("");
  const [bannerImageUrls, setBannerImageUrls] = useState({});
  const [banner, setBanner] = useState([]);
  const [colorPickers, setColorPickers] = useState({
    primary_colors: false,
    secondary_colors: false,
    success_colors: false,
    warning_colors: false,
    danger_colors: false,
    background_colors: false,
  });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [editingBanners, setEditingBanners] = useState({});

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setHoveredIndex(index);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newBanners = [...banner];
    const draggedItem = newBanners[draggedIndex];
    newBanners.splice(draggedIndex, 1);
    newBanners.splice(index, 0, draggedItem);

    setBanner(newBanners);
    form.setFieldsValue({ banners: newBanners });
    setDraggedIndex(null);
    setHoveredIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setHoveredIndex(null);
  };

  const toggleEditBanner = (index) => {
    setEditingBanners((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  async function getDataList() {
    setLoading(true);
    try {
      const res = await GetDataFromServer(AGENT_LIST, access_token);

      setAgentData(res.data);
      if (res.data?.ui?.banners) {
        setBanner(res.data.ui?.banners || []);
      }
      if (res.data?.ui?.logo_url) {
        setImageUrl(res.data.ui.logo_url);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error(error?.response?.data?.error || "Connection Error");
    }
  }

  useEffect(() => {
    getDataList();
  }, []);

  const formatColor = (color) => {
    if (!color) return "";
    return color.startsWith("#") ? color : `#${color}`;
  };

  const getColorValue = (colorName) => {
    return formatColor(agentData?.ui?.[colorName]?.[0] || "");
  };

  useEffect(() => {
    if (agentData) {
      form.setFieldsValue({
        primary_colors: getColorValue("primary_colors"),
        secondary_colors: getColorValue("secondary_colors"),
        success_colors: getColorValue("success_colors"),
        warning_colors: getColorValue("warning_colors"),
        danger_colors: getColorValue("danger_colors"),
        background_colors: getColorValue("background_colors"),
        banners: banner,
        logo_url: imageUrl,
      });
    }
  }, [agentData, form, banner]);

  const toggleColorPicker = (field) => {
    setColorPickers((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleColorChange = (field, color) => {
    const hexColor = color.toHexString();
    form.setFieldsValue({ [field]: hexColor });
    toggleColorPicker(field);
  };

  const submitHandler = async (values) => {
    setLoading(true);

    const colorFields = [
      "primary_colors",
      "secondary_colors",
      "success_colors",
      "warning_colors",
      "danger_colors",
      "background_colors",
    ];

    const allBanners = [
      ...banner.map((item, index) => ({
        order: index,
        url: item.url || "",
        web_link: item.web_link || "",
        app_link: item.app_link || "",
      })),

      ...(values.newBannersInput || []).map((item, index) => ({
        order: banner.length + index,
        url: bannerImageUrls[index] || "",
        web_link: item.web_link || "",
        app_link: item.app_link || "",
      })),
    ];

    const payload = {
      ...Object.fromEntries(
        colorFields.map((field) => [
          field,
          values[field] ? [values[field].replace("#", "")] : [],
        ])
      ),
      logo_url: imageUrl,
      banners: allBanners.filter((b) => b.url),
    };

    try {
      await updateUserDataOnServer(
        AGENT_UPDATE_UI,
        payload,
        access_token,
        csrf_token
      );

      form.setFieldsValue({
        newBannersInput: undefined,
      });

      setBannerImageUrls({});
      setEditingBanners({});

      toast.success("UI updated successfully");
      getDataList();
    } catch (error) {
      toast.error(error?.response?.data?.message?.[0] || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const colorFields = [
    { name: "primary_colors", label: "Primary Color" },
    { name: "secondary_colors", label: "Secondary Color" },
    { name: "success_colors", label: "Success Color" },
    { name: "warning_colors", label: "Warning Color" },
    { name: "danger_colors", label: "Danger Color" },
    { name: "background_colors", label: "Background Color" },
  ];

  const rows = [];
  for (let i = 0; i < colorFields.length; i += 2) {
    rows.push(colorFields.slice(i, i + 2));
  }

  const removeBanner = (index) => {
    if (banner.length <= 1) {
      toast.error("You must keep at least one banner");
      return;
    }     

    const newBanners = [...banner];
    newBanners.splice(index, 1);
    setBanner(newBanners);
      
    setBannerImageUrls((prev) => {
      const newUrls = { ...prev };
      delete newUrls[index];
      return newUrls;
    });

    form.setFieldsValue({ banners: newBanners });
  };

  const updateBanner = (index, field, value) => {
    const newBanners = [...banner];
    newBanners[index] = {
      ...newBanners[index],
      [field]: value,
    };
    setBanner(newBanners);
    form.setFieldsValue({ banners: newBanners });
  };

  return (
    <Row gutter={[16, 24]}>
      <Col
        span={12}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "start",
          alignItems: "center",
        }}
      >
        <UserInfo
          logoUrl={imageUrl}
          bannerInfo={banner}
          colors={{
            primary:
              form.getFieldValue("primary_colors") ||
              getColorValue("primary_colors"),
            secondary:
              form.getFieldValue("secondary_colors") ||
              getColorValue("secondary_colors"),
            success:
              form.getFieldValue("success_colors") ||
              getColorValue("success_colors"),
            warning:
              form.getFieldValue("warning_colors") ||
              getColorValue("warning_colors"),
            danger:
              form.getFieldValue("danger_colors") ||
              getColorValue("danger_colors"),
            background:
              form.getFieldValue("background_colors") ||
              getColorValue("background_colors"),
          }}
        />
      </Col>

      <Col span={12}>
        <Form form={form} layout="vertical" onFinish={submitHandler}>
          {rows.map((row, rowIndex) => (
            <Row gutter={16} key={rowIndex}>
              {row.map(({ name, label }) => {
                const currentColor =
                  form.getFieldValue(name) || getColorValue(name);
                const displayColor = formatColor(currentColor);

                return (
                  <Col span={12} key={name}>
                    <Form.Item
                      name={name}
                      label={label}
                      rules={[
                        { required: true, message: `Please select ${label}` },
                      ]}
                    >
                      <Space>
                        <Select
                          style={{ width: "180px" }}
                          placeholder={`Select ${label}`}
                          onClick={() => toggleColorPicker(name)}
                          dropdownRender={() => null}
                          value={displayColor}
                          suffixIcon={null}
                          onChange={(value) => {
                            const hex = value.startsWith("#")
                              ? value
                              : `#${value}`;
                            form.setFieldsValue({ [name]: hex });
                          }}
                        />
                        {colorPickers[name] && (
                          <ColorPicker
                            open
                            onOpenChange={(open) =>
                              !open && toggleColorPicker(name)
                            }
                            onChangeComplete={(color) =>
                              handleColorChange(name, color)
                            }
                            disabledAlpha
                            value={displayColor}
                                                    />
                        )}
                        {displayColor && (
                          <div
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: 4,
                              backgroundColor: displayColor,
                              border: "1px solid #d9d9d9",
                            }}
                          />
                        )}
                      </Space>
                    </Form.Item>
                  </Col>
                );
              })}
            </Row>
          ))}

          <Row gutter={[16, 24]}>
            <Col>
              <Form.Item name="logo_url" label="Logo">
                <ImgCrop fillColor="rgba(0, 0, 0, 0)" aspect={250 / 150}>
                  <Upload
                    name="logo_url"
                    accept=".jpg,.jpeg,.png"
                    listType="picture-card"
                    showUploadList={false}
                    style={{
                      width: "200px",
                      height: "200px",
                    }}
                    customRequest={(option) =>
                      uploadImages({
                        ...option,
                        access_token,
                        csrf_token,
                        folder_name: "UI_IMAGES",
                        image: imageUrl,
                        setImage: setImageUrl,
                      })
                    }
                    beforeUpload={(file) => beforeUpload(file, 1)}
                    isImageUrl={true}
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        style={{
                          height: "100px",
                          width: "100px",
                          objectFit: "contain",
                          objectPosition: "center",
                        }}
                      />
                    ) : (
                      UploadButton(loading)
                    )}
                  </Upload>
                </ImgCrop>
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item name="banners" label="Banners">
                <Row gutter={[16]}>
                  {Array.isArray(banner) &&
                    banner.map((item, index) => (
                      <Col
                        span={12}
                        key={`existing-${index}`}
                        style={{ marginBottom: 16 }}
                      >
                        <Card
                          styles={{ body: { padding: 16 } }}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                          style={{
                            opacity: draggedIndex === index ? 0.5 : 1,
                            border:
                              hoveredIndex === index
                                ? "2px dashed #1890ff"
                                : "1px solid #f0f0f0",
                          }}
                        >
                          {editingBanners[index] ? (
                            <div>
                              <Form.Item
                                label="Banner Image"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please upload banner image",
                                  },
                                ]}
                              >
                                <ImgCrop rotationSlider aspect={1000 / 500}>
                                  <Upload
                                    name={`banner_${index}`}
                                    accept=".jpg,.jpeg,.png"
                                    listType="picture-card"
                                    showUploadList={false}
                                    customRequest={(option) =>
                                      uploadImages({
                                        ...option,
                                        access_token,
                                        csrf_token,
                                        folder_name: "UI_IMAGES",
                                        image: item.url || "",
                                        setImage: (url) =>
                                          updateBanner(index, "url", url),
                                      })
                                    }
                                    beforeUpload={(file) =>
                                      beforeUpload(file, 1)
                                    }
                                  >
                                    {item.url ? (
                                      <div
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          overflow: "hidden",
                                          borderRadius: "8px",
                                        }}
                                      >
                                        <img
                                          src={item.url}
                                          alt="banner_image"
                                          style={{
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      UploadButton(loading)
                                    )}
                                  </Upload>
                                </ImgCrop>
                              </Form.Item>

                              <Form.Item label="Web Link URL">
                                <Input
                                  placeholder="Enter web link URL"
                                  value={item.web_link}
                                  onChange={(e) =>
                                    updateBanner(
                                      index,
                                      "web_link",
                                      e.target.value
                                    )
                                  }
                                />
                              </Form.Item>

                              <Form.Item label="App Link URL">
                                <Input
                                  placeholder="Enter app link URL"
                                  value={item.app_link}
                                  onChange={(e) =>
                                    updateBanner(
                                      index,
                                      "app_link",
                                      e.target.value
                                    )
                                  }
                                />
                              </Form.Item>

                              <Space>
                                <Button onClick={() => toggleEditBanner(index)}>
                                  Cancel
                                </Button>
                                <Button
                                  type="primary"
                                  onClick={() => {
                                    const updatedBanner = {
                                      url: banner[index].url,
                                      web_link: banner[index].web_link,
                                      app_link: banner[index].app_link,
                                    };

                                    const newBanners = [...banner];
                                    newBanners[index] = updatedBanner;
                                    setBanner(newBanners);

                                    form.setFieldsValue({
                                      banners: newBanners,
                                    });

                                    toggleEditBanner(index);
                                  }}
                                >
                                  Save
                                </Button>
                              </Space>
                            </div>
                          ) : (
                            <div>
                              <div
                                style={{
                                  width: "100%",
                                  paddingBottom: "50%",
                                  position: "relative",
                                  overflow: "hidden",
                                  borderRadius: "8px",
                                }}
                              >
                                <img
                                  src={item.url}
                                  alt="banner_image"
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </div>
                              <Row
                                gutter={[0, 16]}
                                style={{ marginTop: "20px" }}
                              >
                                <Col span={8}>
                                  <Text strong>Web Link:</Text>
                                </Col>
                                <Col span={16}>
                                  <Text>{item.web_link || "-"}</Text>
                                </Col>
                                <Col span={8}>
                                  <Text strong>App Link:</Text>
                                </Col>
                                <Col span={16}>
                                  <Text>{item.app_link || "-"}</Text>
                                </Col>
                                <Col span={24} style={{ marginTop: "20px" }}>
                                  <Space>
                                    <Button
                                      type="primary"
                                      onClick={() => toggleEditBanner(index)}
                                      size={ui_size}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      onClick={() => removeBanner(index)}
                                      disabled={banner.length <= 1}
                                      type="primary"
                                      danger
                                      size={ui_size}
                                    >
                                      Delete
                                    </Button>
                                  </Space>
                                </Col>
                              </Row>
                            </div>
                          )}
                        </Card>
                      </Col>
                    ))}

                  <Form.List name="newBannersInput">
                    {(fields, { add, remove }) => {
                      const totalItems = banner.length + fields.length;
                      const itemsPerRow = 2;
                      const emptySlots = totalItems % itemsPerRow;
                      const availableSlots =
                        emptySlots > 0 ? itemsPerRow - emptySlots : 0;

                      return (
                        <>
                          {fields.map(({ key, name, ...restField }, index) => {
                            const span =
                              index < availableSlots
                                ? 24 / (emptySlots + 1)
                                : 12;

                            return (
                              <Col
                                span={span}
                                key={key}
                                style={{ marginBottom: "16px" }}
                              >
                                <Card
                                  styles={{ body: { padding: 16 } }}
                                  style={{
                                    border: "1px solid #f0f0f0",
                                    height: "100%",
                                  }}
                                >
                                  <Row justify="space-between">
                                    <Col flex="auto">
                                      <Form.Item
                                        {...restField}
                                        name={[name, "url"]}
                                        label="Banner Image"
                                        rules={[
                                          {
                                            validator: (_, value) => {
                                              if (!bannerImageUrls[name]) {
                                                return Promise.reject(
                                                  "Please upload banner image"
                                                );
                                              }
                                              return Promise.resolve();
                                            },
                                          },
                                        ]}
                                      >
                                        <ImgCrop
                                          rotationSlider
                                          aspect={1000 / 500}
                                        >
                                          <Upload
                                            name={`banners_${name}`}
                                            accept=".jpg,.jpeg,.png"
                                            listType="picture-card"
                                            showUploadList={false}
                                            customRequest={(option) =>
                                              uploadImages({
                                                ...option,
                                                access_token,
                                                csrf_token,
                                                folder_name: "UI_IMAGES",
                                                image:
                                                  bannerImageUrls[name] || "",
                                                setImage: (url) => {
                                                  setBannerImageUrls(
                                                    (prev) => ({
                                                      ...prev,
                                                      [name]: url,
                                                    })
                                                  );
                                                  form.setFieldsValue({
                                                    newBannersInput: {
                                                      ...form.getFieldValue(
                                                        "newBannersInput"
                                                      ),
                                                      [name]: {
                                                        ...form.getFieldValue(
                                                          "newBannersInput"
                                                        )?.[name],
                                                        url: url,
                                                      },
                                                    },
                                                  });
                                                },
                                              })
                                            }
                                            beforeUpload={(file) =>
                                              beforeUpload(file, 1)
                                            }
                                          >
                                            {bannerImageUrls[name] ? (
                                              <div
                                                style={{
                                                  width: "100%",
                                                  height: "100%",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  overflow: "hidden",
                                                  borderRadius: "8px",
                                                }}
                                              >
                                                <img
                                                  src={bannerImageUrls[name]}
                                                  alt="banner_image"
                                                  style={{
                                                    maxWidth: "100%",
                                                    maxHeight: "100%",
                                                    objectFit: "contain",
                                                  }}
                                                />
                                              </div>
                                            ) : (
                                              UploadButton(loading)
                                            )}
                                          </Upload>
                                        </ImgCrop>
                                      </Form.Item>
                                    </Col>
                                    <Col>
                                      <Button
                                        type="primary"
                                        danger
                                        onClick={() => remove(name)}
                                      >
                                        Delete
                                      </Button>
                                    </Col>
                                  </Row>

                                  <Form.Item
                                    {...restField}
                                    name={[name, "web_link"]}
                                    label="Web Link URL"
                                    rules={[{ required: true }]}
                                  >
                                    <Input
                                      size={ui_size}
                                      placeholder="Enter web link URL"
                                    />
                                  </Form.Item>

                                  <Form.Item
                                    {...restField}
                                    name={[name, "app_link"]}
                                    label="App Link URL"
                                    rules={[{ required: true }]}
                                  >
                                    <Input
                                      size={ui_size}
                                      placeholder="Enter app link URL"
                                    />
                                  </Form.Item>
                                </Card>
                              </Col>
                            );
                          })}

                          <Col span={24}>
                            <Button
                              type="primary"
                              block
                              onClick={() =>
                                add({ url: "", web_link: "", app_link: "" })
                              }
                              icon={<PlusOutlined />}
                              style={{ marginTop: 16 }}
                            >
                              Add Banner
                            </Button>
                          </Col>
                        </>
                      );
                    }}
                  </Form.List>
                </Row>
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24} style={{ marginTop: "20px" }}>
              <Button
                size={ui_size}
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                Submit
              </Button>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
}
